import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../services/supabase';

export interface OrderItem {
  id?: string;
  product_id?: string;
  name: string;
  qty: number;
  unit_price: number;
  notes?: string | null;
}

export interface OrderStateHistoryEntry {
  state: string;
  at: string;
  by?: string | null;
}

export interface TableDetail {
  id: string;
  table_number: number;
  capacity: number;
  section: string | null;
  nfc_uid: string | null;
  nfc_active: boolean;
  current_order: {
    id: string;
    created_at: string;
    total_amount: number;
    payment_method: string | null;
    fiscal_status: string | null;
    employee_name: string | null;
    items: OrderItem[];
    tip_amount: number;
    state: string | null;
    state_history: OrderStateHistoryEntry[];
  } | null;
}

const ORDER_STATES = ['pending', 'confirmed', 'preparing', 'ready', 'served'] as const;
export type OrderState = typeof ORDER_STATES[number];

export function nextOrderState(current: string | null | undefined): OrderState | null {
  if (!current) return 'confirmed';
  const idx = (ORDER_STATES as readonly string[]).indexOf(current);
  if (idx < 0 || idx >= ORDER_STATES.length - 1) return null;
  return ORDER_STATES[idx + 1];
}

export function useTableDetail(tableId: string) {
  const [detail, setDetail] = useState<TableDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    const { data: tableRow } = await supabase
      .from('restaurant_tables')
      .select('id, table_number, capacity, section, nfc_uid, nfc_active')
      .eq('id', tableId)
      .maybeSingle();

    if (!tableRow) { setLoading(false); return; }

    const { data: orders } = await supabase
      .from('orders')
      .select(`
        id, created_at, total_amount, payment_method, fiscal_status, tip_amount,
        items, customer_info, state, state_history,
        employees!orders_employee_id_fkey (full_name)
      `)
      .eq('table_id', tableId)
      .in('status', ['pending', 'open'])
      .order('created_at', { ascending: false })
      .limit(1);

    const o = (orders ?? [])[0] as Record<string, unknown> | undefined;
    const emp = o?.employees as { full_name?: string } | null;

    const td: TableDetail = {
      ...(tableRow as Record<string, unknown>),
      current_order: o
        ? {
            id: o.id as string,
            created_at: o.created_at as string,
            total_amount: (o.total_amount as number) ?? 0,
            payment_method: o.payment_method as string | null,
            fiscal_status: o.fiscal_status as string | null,
            employee_name: emp?.full_name ?? null,
            items: (o.items as OrderItem[] | null) ?? [],
            tip_amount: (o.tip_amount as number) ?? 0,
            state: (o.state as string | null) ?? null,
            state_history: (o.state_history as OrderStateHistoryEntry[] | null) ?? [],
          }
        : null,
    } as TableDetail;

    setDetail(td);
    setLoading(false);
  }, [tableId]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  const closeTable = useCallback(async (orderId: string) => {
    await Promise.all([
      supabase.from('restaurant_tables').update({ status: 'available' }).eq('id', tableId),
      supabase.from('orders').update({ status: 'closed' }).eq('id', orderId),
    ]);
    await fetch();
  }, [tableId, fetch]);

  const updateTip = useCallback(async (orderId: string, tip: number) => {
    await supabase.from('orders').update({ tip_amount: tip }).eq('id', orderId);
    await fetch();
  }, [fetch]);

  const requestHelp = useCallback(async (storeId: string) => {
    await supabase.from('table_requests').insert({ table_id: tableId, store_id: storeId, type: 'help' });
  }, [tableId]);

  const advanceState = useCallback(async (orderId: string, newState: string) => {
    try {
      const { error } = await supabase.functions.invoke('tapnpay-order-advance-state', {
        body: { order_id: orderId, new_state: newState },
      });
      if (error) throw error;
    } catch {
      // Best-effort fallback when edge function is not available
      await supabase
        .from('orders')
        .update({ state: newState })
        .eq('id', orderId);
    }
    await fetch();
  }, [fetch]);

  return { detail, loading, closeTable, updateTip, requestHelp, advanceState, refetch: fetch };
}
